/**
 * Automatic draft points. Pokémon are valued the way a draft board is made: first on their own, then against the
 * meta, since a board is relative to its top threats.
 *
 * On its own, a Pokémon's stats, moves and abilities are judged together:
 * - Offence: how hard its best attack hits (power, accuracy, the attacking stat, STAB and abilities like
 *   Adaptability, Fairy Aura or Drought), how much of the type chart its strong attacks cover (the Draft Planner's
 *   coverage rule), and whether its Speed lets it hit first (or Trick Room suits it).
 * - Support: its utility moves, each weighted by how important it is, and by whether this Pokémon can make good use
 *   of it. Redirection needs bulk, Prankster makes status moves better.
 * - Bulk: HP and defences, after its typing, abilities and recovery.
 * - Abilities: each is tried, and the best counts, with what it does for the team (Intimidate, weather, terrain...).
 * - Items: Pokémon that can hold anything get their best item (Life Orb, a Choice item, Sitrus Berry). Megas can't,
 *   so a Mega is only worth more than its base form holding an item if it's a real upgrade.
 *
 * Then the pool's best Pokémon are its meta, and everything is judged again against them: how hard it hits them,
 * how well it takes their hits (Intimidate only helps against physical ones), and how well it partners them by
 * hitting the Pokémon that wall them.
 *
 * The values are ranked across the pool and turned into points shaped like a hand-made board.
 */
import {
	COVERAGE_THRESHOLD, MoveTrait, UNRELIABLE_MOVES, effectivePower, moveMultiplier, toID, typeMultiplier,
	utilityValue, type MoveInfo, type PokemonData, type Species,
} from '@ogreclan/pokemon-core';

/**
 * The shape of a hand-made draft board: at each point of a pool ranked from worst (0) to best (1), how far up the
 * points range a Pokémon is (0 = the cheapest, 1 = the most expensive). Averaged from OgreClan's sample boards, so
 * most of a pool is cheap, and the top two tiers are kept for the best 1-2% (the game-breaking Pokémon).
 */
export const BOARD_SHAPE: readonly (readonly [rank: number, share: number])[] = [
	[0, 0], [0.1, 0], [0.15, 0.03], [0.2, 0.03], [0.25, 0.059], [0.3, 0.059], [0.35, 0.089], [0.4, 0.098],
	[0.45, 0.128], [0.5, 0.168], [0.55, 0.207], [0.6, 0.238], [0.65, 0.305], [0.7, 0.365], [0.75, 0.434],
	[0.8, 0.493], [0.85, 0.583], [0.88, 0.622], [0.91, 0.692], [0.93, 0.723], [0.95, 0.772], [0.96, 0.792],
	[0.97, 0.839], [0.98, 0.882], [0.985, 0.902], [0.99, 0.946], [0.995, 0.969], [1, 1],
];

/** Where a rank (0 = worst, 1 = best) falls on BOARD_SHAPE */
export function boardShare(rank: number) {
	for (let i = 1; i < BOARD_SHAPE.length; i++) {
		const [r1, s1] = BOARD_SHAPE[i]!;
		if (rank > r1) continue;
		const [r0, s0] = BOARD_SHAPE[i - 1]!;
		return r1 === r0 ? s1 : s0 + (s1 - s0) * (rank - r0) / (r1 - r0);
	}
	return 1;
}

export interface PricingOptions {
	minPoints: number;
	maxPoints: number;
	/** Whether the league's format allows a move (all moves, if not given) */
	moveAllowed?: (moveId: string) => boolean;
	/** Whether the league's format allows an ability (all abilities, if not given) */
	abilityAllowed?: (abilityId: string) => boolean;
	/** Doubles (the default) values Fake Out, redirection and spread moves; singles values hazards and pivoting */
	gameType?: 'doubles' | 'singles';
	/** What the rest of the pool is like (autoPoints works it out with poolContext) */
	pool?: PoolContext;
}

type ScoringOptions = Omit<PricingOptions, 'minPoints' | 'maxPoints'>;

/** What a draft pool is like, so each Pokémon is valued against the rest of it */
export interface PoolContext {
	/** type -> how common it is among the pool's Pokémon, so how often it's attacked (1 = average) */
	typeWeights: Record<string, number>;
	/** type -> how common its attacks are in the pool: STABs, and spread moves most of all (1 = average) */
	attackWeights: Record<string, number>;
	/** utility move id -> the share of the pool that can use it (a move most of the pool has is worth less in a draft) */
	moveShares: Record<string, number>;
}

/** What a Pokémon's value is made of, for showing why it costs what it does */
export interface SpeciesScore {
	/** Its overall value (what the points are ranked on) */
	value: number;
	/** How hard it hits, before Speed (1 = a 100 power STAB attack from 100 in the attacking stat) */
	offence: number;
	/** What its best three utility moves do for the team (1 = one very good move, like Fake Out) */
	support: number;
	/** How much it can take (1 = 100 HP and 100 in both defences, with neutral typing) */
	bulk: number;
	/** How much its Speed helps its offence (0.72-1.2), counting Trick Room for slow Pokémon */
	speed: number;
	/** The ability its value assumes */
	ability: string;
	/** The item its value assumes ('' for Megas and other Pokémon that need a particular item) */
	item: string;
	/**
	 * Against the pool's meta: the share of HP its best hits do to the meta's best Pokémon, the share theirs do to it,
	 * and the share its hits do to the Pokémon that wall them
	 */
	meta?: { dealt: number, taken: number, partnering: number, walled: number, threatened: number };
}

/* Stats at level 50, the usual level for doubles */

/** What a fully invested stat adds to the base stat at level 50 (252 EVs or 32 stat points) */
const INVESTED = 52;
/** HP and defences, assuming some investment */
const HP_BONUS = 90;
const DEFENCE_BONUS = 36;

/** A 100 power STAB attack from a Pokémon with 100 in the attacking stat */
const REFERENCE_DAMAGE = 100 * 1.5 * (100 + INVESTED);
/** A Pokémon with 100 HP and 100 in both defences */
const REFERENCE_BULK = (100 + HP_BONUS) * (100 + DEFENCE_BONUS);
/** A very good utility move (Fake Out is 8) */
const REFERENCE_SUPPORT = 8;
/** Turns an attack's damage into a share of the target's HP: the damage formula's 22/50 at level 50 */
const DAMAGE_SCALE = 22 / 50;

/** How much more spread attacks are worth in doubles (each foe takes 3/4 of the damage) */
const SPREAD_BONUS = { foes: 1.3, withAlly: 1.3 };

/* Abilities */

type TypeAndMove = (move: MoveInfo, type: string) => number;
const ofType = (boosted: string, multiplier: number): TypeAndMove => (_, type) => type === boosted ? multiplier : 1;
const withTrait = (trait: number, multiplier: number): TypeAndMove => move => move.traits & trait ? multiplier : 1;

/** Abilities that make some attacks stronger */
const ATTACK_ABILITIES: Readonly<Record<string, TypeAndMove>> = {
	toughclaws: withTrait(MoveTrait.contact, 1.3),
	ironfist: withTrait(MoveTrait.punch, 1.2),
	strongjaw: withTrait(MoveTrait.bite, 1.5),
	megalauncher: withTrait(MoveTrait.pulse, 1.5),
	punkrock: withTrait(MoveTrait.sound, 1.3),
	sharpness: withTrait(MoveTrait.slicing, 1.5),
	sheerforce: withTrait(MoveTrait.secondary, 1.3),
	reckless: withTrait(MoveTrait.recoil, 1.2),
	technician: move => move.basePower > 0 && move.basePower <= 60 ? 1.5 : 1,
	analytic: () => 1.1,
	parentalbond: () => 1.25,
	// Kingambit's attacks once teammates have fainted
	supremeoverlord: () => 1.2,
	// with Booster Energy, or in sun or Electric Terrain
	protosynthesis: () => 1.1,
	quarkdrive: () => 1.1,
	neuroforce: () => 1.1,
	transistor: ofType('Electric', 1.3),
	dragonsmaw: ofType('Dragon', 1.5),
	steelworker: ofType('Steel', 1.5),
	steelyspirit: ofType('Steel', 1.5),
	rockypayload: ofType('Rock', 1.5),
	waterbubble: ofType('Water', 2),
	// 1.5x on paper, but its Fire attacks are easy to play around (OgreClan's boards rate it as situational)
	firemane: ofType('Fire', 1.25),
	fairyaura: ofType('Fairy', 1.33),
	darkaura: ofType('Dark', 1.33),
	electricsurge: ofType('Electric', 1.3),
	hadronengine: ofType('Electric', 1.3),
	psychicsurge: ofType('Psychic', 1.3),
	grassysurge: ofType('Grass', 1.3),
	orichalcumpulse: ofType('Fire', 1.5),
	// weather can be replaced by the opponent's, so it doesn't count fully
	drought: (_, type) => type === 'Fire' ? 1.4 : type === 'Water' ? 0.6 : 1,
	megasol: (_, type) => type === 'Fire' ? 1.5 : type === 'Water' ? 0.5 : 1,
	drizzle: (_, type) => type === 'Water' ? 1.4 : type === 'Fire' ? 0.6 : 1,
};

/** Abilities that multiply an attacking stat */
const STAT_ABILITIES: Readonly<Record<string, { physical?: number, special?: number }>> = {
	hugepower: { physical: 2 },
	purepower: { physical: 2 },
	gorillatactics: { physical: 1.3 },
	// 1.5x Attack, but 80% accuracy
	hustle: { physical: 1.2 },
	orichalcumpulse: { physical: 1.33 },
	hadronengine: { special: 1.33 },
	// needs sun
	solarpower: { special: 1.2 },
};

/** Abilities that turn Normal attacks into another type, with 1.2x power */
const TYPE_CHANGING_ABILITIES: Readonly<Record<string, string>> = {
	aerilate: 'Flying', pixilate: 'Fairy', refrigerate: 'Ice', galvanize: 'Electric', dragonize: 'Dragon',
};

/** The weather an ability sets, which Weather Ball takes the type of */
const WEATHER_TYPES: Readonly<Record<string, string>> = {
	drought: 'Fire', megasol: 'Fire', drizzle: 'Water', sandstream: 'Rock', snowwarning: 'Ice',
};

/** Abilities that make a Pokémon harder to KO: [physical, special] multipliers on its defences */
const BULK_ABILITIES: Readonly<Record<string, [number, number]>> = {
	furcoat: [1.6, 1], fluffy: [1.25, 1], icescales: [1, 1.4], multiscale: [1.2, 1.2], shadowshield: [1.2, 1.2],
	filter: [1.1, 1.1], solidrock: [1.1, 1.1], prismarmor: [1.1, 1.1], stamina: [1.25, 1],
	auraguard: [1.2, 1], disguise: [1.15, 1.15], marvelscale: [1.05, 1], terashell: [1.1, 1.1],
	regenerator: [1.08, 1.08], sturdy: [1.04, 1.04], purifyingsalt: [1.08, 1.08], spicyspray: [1.2, 1],
};

/**
 * What an ability does for the team beyond the Pokémon's own numbers (in value units: a good support Pokémon's
 * support is about 1). Intimidate's is scaled by how many of the meta's best Pokémon attack physically.
 */
const ABILITY_BONUS: Readonly<Record<string, number>> = {
	// Psychic Terrain blocks Fake Out and other priority attacks against the whole team
	intimidate: 0.3, drizzle: 0.25, drought: 0.25, sandstream: 0.12, snowwarning: 0.15, psychicsurge: 0.45,
	electricsurge: 0.12, grassysurge: 0.15, mistysurge: 0.1, hadronengine: 0.1, orichalcumpulse: 0.1,
	friendguard: 0.2, commander: 0.2, armortail: 0.12, queenlymajesty: 0.12, dazzling: 0.12, goodasgold: 0.12,
	// trapping (with Perish Song or a partner's attacks) is game-breaking
	shadowtag: 0.5, arenatrap: 0.08, unseenfist: 0.12, piercingdrill: 0.02, magicbounce: 0.04, innerfocus: 0.08,
	owntempo: 0.03, oblivious: 0.03, scrappy: 0.05, guarddog: 0.08, defiant: 0.25, competitive: 0.2,
	clearbody: 0.04, whitesmoke: 0.04, fullmetalbody: 0.04, mirrorarmor: 0.06, trace: 0.06, imposter: 0.3,
	hospitality: 0.15, healer: 0.03, lightningrod: 0.08, stormdrain: 0.08, flowerveil: 0.02, aromaveil: 0.05,
	moody: 0.1, stancechange: 0.05, beastboost: 0.06, moxie: 0.06, chillingneigh: 0.06, grimneigh: 0.06,
	asoneglastrier: 0.06, asonespectrier: 0.06, prankster: 0.15, berserk: 0.03, angershell: 0.05,
	poisonpuppeteer: 0.05, toxicchain: 0.06, costar: 0.05, zerotohero: 0.1, opportunist: 0.04, telepathy: 0.02,
	symbiosis: 0.02, windrider: 0.05, sweetveil: 0.03, pastelveil: 0.03, illusion: 0.05, serenegrace: 0.05,
	// Fairy Aura boosts its partner's Fairy attacks too, and on Mega Floette it's game-breaking
	triage: 0.05, galewings: 0.05, fairyaura: 0.4, darkaura: 0.1, eelevate: 0.04, cursedbody: 0.02,
	spicyspray: 0.12, toxicdebris: 0.03, stamina: 0.1,
	// once its item is used, its Speed doubles and it outspeeds nearly everything (Sneasler)
	unburden: 0.3,
};

/**
 * Abilities that only help a partner, so they're worth nothing in singles (Hospitality has no ally to heal, Friend
 * Guard no ally to protect)
 */
const DOUBLES_ONLY_ABILITIES = new Set([
	'hospitality', 'friendguard', 'telepathy', 'commander', 'healer', 'symbiosis', 'flowerveil', 'powerspot',
	'battery', 'plus', 'minus', 'curiousmedicine', 'steelyspirit', 'costar', 'receiver', 'powerofalchemy',
	'lightningrod', 'stormdrain',
]);

/** What an ability does for the team, in this game type (see ABILITY_BONUS) */
function abilityBonus(ability: string, gameType: 'doubles' | 'singles') {
	const bonus = ABILITY_BONUS[ability] ?? 0;
	if (gameType === 'doubles') return bonus;
	if (DOUBLES_ONLY_ABILITIES.has(ability)) return 0;
	// in singles, Intimidate hits one foe, not two
	return ability === 'intimidate' ? bonus / 2 : bonus;
}

/** Abilities that hold a Pokémon back, as multipliers on its value */
const ABILITY_PENALTY: Readonly<Record<string, number>> = {
	truant: 0.45, slowstart: 0.45, defeatist: 0.85, klutz: 0.85, stall: 0.95, wimpout: 0.95, emergencyexit: 0.95,
};

/** Abilities that make it faster than its Speed stat says */
const SPEED_ABILITIES: Readonly<Record<string, number>> = {
	// doubled in their weather or terrain, but only while a teammate keeps it up
	speedboost: 1.5, swiftswim: 1.3, chlorophyll: 1.3, sandrush: 1.3, slushrush: 1.3, surgesurfer: 1.25,
	unburden: 1.25,
};

/** Moves that hit perfectly in the weather an ability sets */
const WEATHER_ACCURACY: Readonly<Record<string, readonly string[]>> = {
	drizzle: ['thunder', 'hurricane', 'bleakwindstorm', 'wildboltstorm', 'sandsearstorm'],
	snowwarning: ['blizzard'],
};

/* Moves */

const REDIRECTION = new Set(['followme', 'ragepowder', 'spotlight']);
const PROTECTION = new Set(['protect', 'detect', 'spikyshield', 'kingsshield', 'banefulbunker', 'silktrap', 'burningbulwark', 'obstruct']);
const PHYSICAL_SETUP = new Set([
	'swordsdance', 'dragondance', 'bulkup', 'victorydance', 'shiftgear', 'coil', 'curse', 'noretreat', 'bellydrum',
	'filletaway', 'howl', 'tidyup', 'honeclaws',
]);
const SPECIAL_SETUP = new Set(['nastyplot', 'calmmind', 'quiverdance', 'tailglow', 'geomancy', 'takeheart']);
const MIXED_SETUP = new Set(['shellsmash', 'growth', 'workup', 'clangoroussoul', 'agility', 'rockpolish', 'autotomize']);
/** Attacks that raise the user's attacking stat, so they snowball like a setup move: their value as one */
const BOOSTING_ATTACKS: Readonly<Record<string, number>> = {
	electroshot: 3.5, meteorbeam: 3, poweruppunch: 1.5, torchsong: 2.5, flamecharge: 1, trailblaze: 1, rapidspin: 0.5,
};
const RECOVERY = new Set([
	'recover', 'roost', 'slackoff', 'softboiled', 'milkdrink', 'moonlight', 'morningsun', 'synthesis', 'shoreup',
	'strengthsap', 'healorder', 'lunarblessing', 'junglehealing', 'lifedew', 'matchagotcha',
]);
/** Attacks whose side effects are worth more than their power says */
const MOVE_EFFECT_BONUS: Readonly<Record<string, number>> = {
	// 50% to poison, paralyse or put to sleep
	direclaw: 1.3,
};
/** Attacks that lock the user in and hit a random foe: much worse in doubles */
const RAMPAGE_MOVES = new Set(['outrage', 'thrash', 'petaldance', 'ragingfury']);
const SCREENS = new Set(['reflect', 'lightscreen', 'auroraveil']);

/** In singles, these moves are worth something else than in doubles (the rest keep their doubles value) */
const SINGLES_UTILITY: Readonly<Record<string, number>> = {
	stealthrock: 6, spikes: 4, toxicspikes: 2.5, stickyweb: 4, defog: 3.5, rapidspin: 3.5, mortalspin: 3.5,
	courtchange: 2, uturn: 4, voltswitch: 4, flipturn: 3.5, partingshot: 3.5, teleport: 2, shedtail: 4,
	followme: 0, ragepowder: 0, spotlight: 0, fakeout: 1, tailwind: 1.5, trickroom: 2, wideguard: 0, quickguard: 0,
	helpinghand: 0, allyswitch: 0, afteryou: 0, instruct: 0, coaching: 0, decorate: 0, pollenpuff: 0.5,
	healpulse: 0, floralhealing: 0, icywind: 1, electroweb: 0.5, snarl: 1, knockoff: 4, toxic: 3.5, roar: 2,
	whirlwind: 2, haze: 2.5, recover: 4, roost: 4, slackoff: 4, softboiled: 4, milkdrink: 4, moonlight: 3,
	synthesis: 3, morningsun: 3, shoreup: 4, strengthsap: 4, wish: 3, protect: 1.5, perishsong: 1, beatup: 0,
	healingwish: 2, lunardance: 2, memento: 2, encore: 3.5, taunt: 3.5, trick: 3, switcheroo: 3, destinybond: 2,
};

function clamp(value: number, min: number, max: number) {
	return Math.min(max, Math.max(min, value));
}

interface Context {
	data: PokemonData;
	gameType: 'doubles' | 'singles';
	pool?: PoolContext;
}

function typeWeight(context: Context, type: string) {
	return context.pool?.typeWeights[type] ?? 1;
}

function attackWeight(context: Context, type: string) {
	return context.pool?.attackWeights[type] ?? 1;
}

/** How much a utility move is worth in a draft, given how much of the pool can also use it */
function scarcity(context: Context, moveId: string) {
	const share = context.pool?.moveShares[moveId];
	return share === undefined ? 1 : clamp(1.3 - 0.9 * Math.sqrt(share), 0.4, 1.3);
}

/* Attacks */

/** One of a Pokémon's attacks, as its ability and stats make it */
export interface Attack {
	/** For valuing: power, the attacking stat, STAB, abilities, and the bonus for spread moves */
	damage: number;
	/** The damage to one target (spread attacks do 3/4 to each), for matchups */
	perTarget: number;
	type: string;
	category: 'Physical' | 'Special';
	/** Strong enough to count as coverage, by the Draft Planner's rule */
	coverage: boolean;
	priority: boolean;
	move: MoveInfo;
}

function getAttack(species: Species, move: MoveInfo, ability: string, gameType: 'doubles' | 'singles'): Attack | null {
	if (move.category === 'Status' || UNRELIABLE_MOVES.has(move.id) || move.id === 'fakeout') return null;
	let power = effectivePower(move, ability);
	if (!power) return null;
	let type = move.type;
	if (type === 'Normal' && TYPE_CHANGING_ABILITIES[ability]) {
		type = TYPE_CHANGING_ABILITIES[ability];
		power *= 1.2;
	}
	if (ability === 'liquidvoice' && move.traits & MoveTrait.sound) type = 'Water';
	if (move.id === 'weatherball' && WEATHER_TYPES[ability]) {
		// doubled to 100 power, and the weather's type
		type = WEATHER_TYPES[ability];
		power = 100;
	}
	if ((ability === 'noguard' || WEATHER_ACCURACY[ability]?.includes(move.id)) && move.accuracy !== true) {
		power /= move.accuracy / 100;
	}
	// no charging turn in sun
	if ((ability === 'drought' || ability === 'megasol') && ['solarbeam', 'solarblade'].includes(move.id)) power /= 0.6;
	if (ability === 'contrary' && move.traits & MoveTrait.selfDrop) {
		// its own stat drops become boosts: Draco Meteor's raise its attack, so it hits harder each time; Close
		// Combat's raise its defences, which only stops the move costing it anything
		power /= move.traits & MoveTrait.selfAttackDrop ? 0.75 / 1.25 : 0.92;
	}
	power *= MOVE_EFFECT_BONUS[move.id] ?? 1;
	if (gameType === 'doubles' && RAMPAGE_MOVES.has(move.id)) power *= 0.7;
	let spread = move.spread;
	if (move.id === 'expandingforce' && ability === 'psychicsurge') {
		power *= 1.5;
		spread = true;
	}

	// Aegislash attacks in its Blade Forme, whose attacking stats are its Shield Forme's defences
	const stats = species.stats;
	let attackStat = move.category === 'Physical' ? stats.atk : stats.spa;
	if (ability === 'stancechange') attackStat = move.category === 'Physical' ? stats.def : stats.spd;
	if (move.id === 'bodypress') attackStat = stats.def;
	// Foul Play uses the target's Attack
	if (move.id === 'foulplay') attackStat = 120;
	const statBoost = (move.category === 'Physical' ? STAT_ABILITIES[ability]?.physical : STAT_ABILITIES[ability]?.special) ?? 1;
	const abilityBoost = ATTACK_ABILITIES[ability]?.(move, type) ?? 1;
	const stab = species.types.includes(type) || ability === 'protean' || ability === 'libero';
	const stabBoost = stab ? (ability === 'adaptability' ? 2 : 1.5) : 1;
	const base = power * (attackStat + INVESTED) * statBoost * abilityBoost * stabBoost;
	// hitting both foes is worth more than hitting one (less if it hits the ally too)
	const spreadBoost = gameType !== 'doubles' || !spread ? 1 :
		move.traits & MoveTrait.hitsAlly ? SPREAD_BONUS.withAlly : SPREAD_BONUS.foes;
	// the planner's rule: power × accuracy × the attacking stat / 100 reaches the threshold
	const coverage = stab || power * attackStat * statBoost * abilityBoost / 100 >= COVERAGE_THRESHOLD;
	const priority = move.priority > 0 || (move.id === 'grassyglide' && ability === 'grassysurge');
	return {
		damage: base * spreadBoost, perTarget: base * (gameType === 'doubles' && spread ? 0.75 : 1),
		type, category: move.category, coverage, priority, move,
	};
}

interface Offence {
	/** Overall offence (1 = a 100 power STAB attack from 100 in the attacking stat, hitting most types neutrally) */
	value: number;
	/** The best physical and special attacks, for valuing setup moves */
	physical: number;
	special: number;
}

function getOffence(context: Context, attacks: readonly Attack[]): Offence {
	const { data } = context;
	if (!attacks.length) return { value: 0, physical: 0, special: 0 };
	const best = Math.max(...attacks.map(a => a.damage));
	const bestOf = (category: string) => Math.max(0, ...attacks.filter(a => a.category === category).map(a => a.damage));

	// how well its strong attacks hit each type (the pool's common types most), compared with its best attack
	// hitting neutrally
	let coverage = 0;
	let totalWeight = 0;
	for (const defender of data.types) {
		let hit = 0;
		for (const attack of attacks) {
			if (!attack.coverage) continue;
			const multiplier = Math.min(moveMultiplier(data, { ...attack.move, type: attack.type }, [defender]), 2);
			hit = Math.max(hit, attack.damage * multiplier);
		}
		const weight = typeWeight(context, defender);
		coverage += weight * Math.min(hit / best, 1.5);
		totalWeight += weight;
	}
	coverage /= totalWeight;

	let value = best / REFERENCE_DAMAGE * (0.6 + 0.4 * coverage);
	// a strong priority attack picks off weakened and faster Pokémon, more so with STAB
	const priority = attacks.filter(a => a.priority && a.damage >= 0.45 * best);
	if (priority.length) value *= priority.some(a => a.damage >= 0.65 * best) ? 1.15 : 1.08;
	return { value, physical: bestOf('Physical') / REFERENCE_DAMAGE, special: bestOf('Special') / REFERENCE_DAMAGE };
}

/** How much a Speed stat helps: fast is good, and very slow is good under Trick Room */
function speedFactor(speed: number, baseSpeed: number, gameType: 'doubles' | 'singles') {
	const fast = 0.72 + 0.48 * clamp((speed - 40) / 90, 0, 1);
	// only while Trick Room is up, so it counts for less than being fast
	const trickRoom = 0.72 + (gameType === 'doubles' ? 0.2 : 0.1) * clamp((70 - baseSpeed) / 45, 0, 1);
	return Math.max(fast, trickRoom);
}

/* Bulk */

interface Bulk {
	/** HP and defences, after abilities, Eviolite and recovery (1 = 100 HP and 100 in both defences) */
	stats: number;
	/** How good its typing is defensively: the average log2 of how much each type resists it (0 = neutral) */
	typing: number;
	/** The two together, as the Pokémon's overall bulk */
	overall: number;
	/** HP and each defence at level 50, for working out how much a hit does */
	hp: number;
	def: number;
	spd: number;
}

/**
 * How much one attacking type's multiplier helps or hurts a Pokémon's typing (log2, so a resistance is -1): a 4x
 * weakness counts as 1.3 weaknesses, since a strong 2x hit already KOs most Pokémon; immunities count as 4x resists
 */
function typingScore(multiplier: number) {
	if (multiplier > 2) return 1 + 0.3 * Math.log2(multiplier / 2);
	return Math.log2(Math.max(multiplier, 0.25));
}

function getBulk(context: Context, species: Species, ability: string, hasRecovery: boolean, screens = 1): Bulk {
	const { data } = context;
	const stats = species.stats;
	let def = stats.def + DEFENCE_BONUS;
	let spd = stats.spd + DEFENCE_BONUS;
	// Eviolite
	if (species.nfe) {
		def *= 1.5;
		spd *= 1.5;
	}
	const [physical, special] = BULK_ABILITIES[ability] ?? [1, 1];
	def *= physical * screens;
	spd *= special * screens;
	const hp = stats.hp + HP_BONUS;
	const statBulk = hp * Math.sqrt(def * spd) / REFERENCE_BULK * (hasRecovery ? 1.03 : 1);
	// resistances help and weaknesses hurt, to the pool's common attacks most (immunities count as 1/4)
	let typing = 0;
	let totalWeight = 0;
	for (const type of data.types) {
		const weight = attackWeight(context, type);
		typing -= weight * typingScore(typeMultiplier(data, type, species.types, ability));
		totalWeight += weight;
	}
	typing /= totalWeight;
	return { stats: statBulk, typing, overall: statBulk * 2 ** (0.6 * typing), hp, def, spd };
}

/* Setup and support */

function setupKind(moveId: string) {
	return PHYSICAL_SETUP.has(moveId) ? 'physical' : SPECIAL_SETUP.has(moveId) ? 'special' :
		MIXED_SETUP.has(moveId) ? 'mixed' : '';
}

/**
 * How much its best setup move (or snowballing attack, like Electro Shot) adds to its offence, as a multiplier:
 * more for a strong one that boosts the stat it attacks with, on a Pokémon bulky enough to use it
 */
function getSetup(context: Context, moves: readonly MoveInfo[], offence: Offence, bulk: number) {
	const best = Math.max(offence.physical, offence.special);
	if (!best) return 1;
	// setting up is much harder in doubles, where both foes can attack the Pokémon setting up; attacks that boost
	// (Electro Shot) don't spend a turn, so they snowball in doubles too
	const setupWeight = context.gameType === 'singles' ? 0.3 : 0.08;
	const boostingWeight = 0.2;
	let setup = 0;
	for (const move of moves) {
		const boosting = BOOSTING_ATTACKS[move.id];
		const kind = setupKind(move.id) || (boosting ? move.category === 'Physical' ? 'physical' : 'special' : '');
		if (!kind) continue;
		const power = kind === 'physical' ? offence.physical : kind === 'special' ? offence.special : best;
		const weight = boosting ? boostingWeight : setupWeight;
		setup = Math.max(setup, weight * (boosting ?? utilityValue(move.id)) / 4.5 * power / best);
	}
	return 1 + setup * Math.sqrt(clamp(bulk, 0.4, 1.6));
}

function getSupport(context: Context, species: Species, moves: readonly MoveInfo[], ability: string, bulk: number) {
	const { gameType } = context;
	const sturdiness = clamp(bulk, 0.4, 1.6);
	const values: number[] = [];
	for (const move of moves) {
		let value = gameType === 'singles' ? SINGLES_UTILITY[move.id] ?? utilityValue(move.id) : utilityValue(move.id);
		if (!value) continue;
		if (REDIRECTION.has(move.id)) {
			// redirection is only as good as the Pokémon's ability to take the hits, and better still if it heals the
			// partner it's protecting
			value *= sturdiness ** 1.5 * (ability === 'hospitality' ? 1.3 : 1);
		} else if (setupKind(move.id)) {
			// setup moves make it a better attacker (see getSetup), not a support Pokémon
			continue;
		} else {
			// it has to survive to use it
			value *= 0.5 + 0.5 * clamp(bulk, 0.4, 1.5);
			// a fast Tailwind goes up before the opponent can stop it
			if (move.id === 'tailwind') value *= 0.8 + 0.4 * clamp((species.stats.spe - 50) / 80, 0, 1);
			if (move.id === 'trickroom') value *= species.stats.spe <= 60 ? 1.15 : 1;
			if (move.id === 'auroraveil' && ability !== 'snowwarning') value *= 0.35;
			if (move.id === 'growth') value *= ability === 'drought' || ability === 'megasol' ? 1.5 : 0.7;
		}
		if (ability === 'prankster' && move.category === 'Status' && !REDIRECTION.has(move.id) && !PROTECTION.has(move.id)) {
			value *= 1.5;
		}
		values.push(value * scarcity(context, move.id));
	}
	// the three most useful, since a Pokémon has four moves and most run Protect
	return values.sort((a, b) => b - a).slice(0, 3).map(value => value / REFERENCE_SUPPORT);
}

/* Combining the parts */

/** The parts of a Pokémon's value with one of its abilities, before they're weighted */
export interface ValueParts {
	species: string;
	ability: string;
	types: readonly string[];
	/** How hard it hits (see SpeciesScore) */
	offence: number;
	/** Its Speed stat, after abilities like Swift Swim */
	speedStat: number;
	baseSpeed: number;
	/** Its three most useful utility moves' values, best first (1 = a very good one, like Fake Out) */
	supportMoves: number[];
	bulk: Bulk;
	/** Its strongest attack of each type and category, for matchups against the meta */
	attacks: Attack[];
	/** What the ability does for the team (ABILITY_BONUS) */
	abilityBonus: number;
	/** How much the ability holds it back (ABILITY_PENALTY) */
	abilityPenalty: number;
	mega: boolean;
	/** Can hold an item of its choice (not a Mega or other item forme, and fully evolved) */
	holdsItem: boolean;
	/** How much its best item multiplies its attacks' damage (set when it's valued in a pool) */
	itemOffence: number;
	gameType: 'doubles' | 'singles';
}

/**
 * How much each part of a Pokémon counts. Chosen by judgement, then tuned so auto points rank Pokémon like
 * OgreClan's own sample draft boards, and match Larry's feedback on the Reg M-C board (2026-10-08 and 09).
 */
export const VALUE_WEIGHTS = {
	/** Offence counts in proportion (tuning found no point where hitting harder stops mattering) */
	offenceCurve: 1,
	offence: 1,
	support: 1.1,
	/** How much more standout utility moves count than ordinary ones (1 = in proportion to their value) */
	supportCurve: 1,
	/** Bulk beyond the average (up to bulkCap: past that, more bulk changes little), and good defensive typing */
	bulk: 2,
	bulkCap: 1.1,
	typing: 0.8,
	ability: 2,
	/** How much more a Mega's attacking counts (1: a Mega is worth what it does, see megaUpgrade) */
	mega: 1,
	/** How much a Mega's support moves count (it uses up the team's one Mega Evolution) */
	megaSupport: 0.5,
	/** What a Mega gains for each unit of attacking power (offence × Speed) it adds to its base forme */
	megaUpgrade: 1,
	/** A Mega has to beat its base forme holding an item by this much, or it loses the difference */
	megaMargin: 0.15,
	/**
	 * Only one Pokémon can Mega Evolve in a battle, so a pool's Megas compete for it: the best megaSlots Megas keep
	 * their value, and each one below them loses megaDecay more of it (down to megaFloor)
	 */
	megaSlots: 40,
	megaDecay: 0,
	megaFloor: 0.75,
	/** All items' effects are multiplied by this (1 = as ITEMS says) */
	item: 0.75,
	/** How many of the pool's best Pokémon make up its meta */
	metaSize: 24,
	/** How much hitting the meta hard makes a Pokémon's attacks worth */
	matchups: 0.5,
	/** How much hitting the Pokémon that wall the meta makes its attacks worth (partnering the best Pokémon) */
	partnering: 0,
	/** How much taking the meta's hits well counts, for every Pokémon */
	survival: 0,
	/** How much being walled by many of the pool's good Pokémon (easily countered) takes from its attacks */
	counters: 0.5,
	/** How much being hit hard by many of the pool's good Pokémon takes from its value */
	threats: 0,
};
export type ValueWeights = typeof VALUE_WEIGHTS;

/**
 * Items a Pokémon can choose from (Megas and other item formes can't). Choice items lock it into one move, so its
 * utility moves count for little.
 */
const ITEMS = {
	lifeorb: { offence: 1.3, speed: 1, bulk: 0.95, support: 1, survives: 0 },
	choice: { offence: 1.5, speed: 1, bulk: 1, support: 0.25, survives: 0 },
	choicescarf: { offence: 1, speed: 1.5, bulk: 1, support: 0.25, survives: 0 },
	sitrusberry: { offence: 1, speed: 1, bulk: 1.15, support: 1.1, survives: 0 },
	// a frail Pokémon is sure to survive a hit, so it counts as about average bulk
	focussash: { offence: 1, speed: 1, bulk: 1, support: 1, survives: 0.95 },
} as const;
export type ItemChoice = keyof typeof ITEMS | '';

/**
 * The ways a Pokémon can split its moves (besides Protect): mostly attacks with one utility move, two of each, or
 * mostly utility moves. Each gives up some offence for more support.
 */
const ROLES = [
	{ offence: 1, support: [1] },
	{ offence: 0.85, support: [1, 0.6] },
	{ offence: 0.6, support: [1, 0.6, 0.35] },
];

/** The support a Pokémon gives with its best three utility moves */
function supportTotal(moves: readonly number[]) {
	return ROLES[2]!.support.reduce((total, weight, i) => total + weight * (moves[i] ?? 0), 0);
}

/**
 * A Pokémon's value holding an item (or none). `attackScale` is how its attacks fare against the meta (1 before it's
 * compared with the meta).
 */
function valueWithItem(parts: ValueParts, item: ItemChoice, weights: ValueWeights, attackScale = 1) {
	const effect = item ? ITEMS[item] : { offence: 1, speed: 1, bulk: 1, support: 1, survives: 0 };
	const scale = (multiplier: number) => 1 + (multiplier - 1) * weights.item;
	const survives = effect.survives * weights.item;
	const bulk = Math.max(parts.bulk.overall * scale(effect.bulk), survives);
	// an attacker has to survive long enough to attack
	const durability = 0.8 + 0.2 * clamp(bulk, 0, 1.5);
	const speed = speedFactor(parts.speedStat * scale(effect.speed), parts.baseSpeed, parts.gameType);
	// a Mega is worth its damage: spending the one Mega Evolution on a support Pokémon wastes it
	const attacking = weights.offence * (parts.offence * scale(effect.offence)) ** weights.offenceCurve * speed *
		durability * Math.max(attackScale, 0.2) * (parts.mega ? weights.mega : 1);
	// spending the one Mega Evolution on support wastes it
	const support = scale(effect.support) * (parts.mega ? weights.megaSupport : 1);
	// it plays whichever role suits it best
	const role = Math.max(...ROLES.map(r => r.offence * attacking + weights.support * support *
		r.support.reduce((total, weight, i) => total + weight * (parts.supportMoves[i] ?? 0) ** weights.supportCurve, 0)));
	const value = role + weights.bulk * (Math.min(Math.max(parts.bulk.stats * scale(effect.bulk), survives), weights.bulkCap) - 1) +
		weights.typing * parts.bulk.typing + weights.ability * parts.abilityBonus;
	return value * parts.abilityPenalty;
}

/** The item that suits a Pokémon best ('' for Megas and other Pokémon that need a particular item) */
export function bestItem(parts: ValueParts, weights = VALUE_WEIGHTS, attackScale = 1): ItemChoice {
	if (!parts.holdsItem) return '';
	let best: ItemChoice = 'lifeorb';
	for (const item of Object.keys(ITEMS) as (keyof typeof ITEMS)[]) {
		if (valueWithItem(parts, item, weights, attackScale) > valueWithItem(parts, best, weights, attackScale)) best = item;
	}
	return best;
}

/** Combines a Pokémon's parts into its value, holding its best item (see valueWithItem for `attackScale`) */
export function partsValue(parts: ValueParts, weights = VALUE_WEIGHTS, attackScale = 1) {
	return valueWithItem(parts, bestItem(parts, weights, attackScale), weights, attackScale);
}

function getParts(context: Context, species: Species, moves: readonly MoveInfo[], ability: string): ValueParts {
	// Palafin fights in its Hero Form
	const form = ability === 'zerotohero' ? context.data.getSpecies(`${species.id}hero`) ?? species : species;
	const hasRecovery = moves.some(move => RECOVERY.has(move.id));
	const attacks = moves.map(move => getAttack(form, move, ability, context.gameType)).filter((a): a is Attack => !!a);
	const offence = getOffence(context, attacks);
	// screens that go up first (Prankster) make it, and its partner, much harder to KO
	const pranksterScreens = ability === 'prankster' && moves.some(move => SCREENS.has(move.id));
	// with Contrary, attacks like Close Combat raise its defences every time it uses them
	const contraryDefences = ability === 'contrary' &&
		attacks.some(a => a.coverage && a.move.traits & MoveTrait.selfDrop && !(a.move.traits & MoveTrait.selfAttackDrop));
	const bulk = getBulk(context, form, ability, hasRecovery, pranksterScreens ? 1.25 : contraryDefences ? 1.2 : 1);
	// for matchups, the strongest attack of each type and category is enough
	const strongest = new Map<string, Attack>();
	for (const attack of attacks) {
		const key = `${attack.type}${attack.category}`;
		if (attack.coverage && (strongest.get(key)?.perTarget ?? 0) < attack.perTarget) strongest.set(key, attack);
	}
	// Palafin has to switch out once before Zero to Hero turns it into its Hero Form
	const formChange = ability === 'zerotohero' ? 0.75 : 1;
	return {
		species: species.id,
		ability,
		types: form.types,
		offence: offence.value * getSetup(context, moves, offence, bulk.overall) * formChange,
		speedStat: form.stats.spe * (SPEED_ABILITIES[ability] ?? 1),
		baseSpeed: form.stats.spe,
		supportMoves: getSupport(context, form, moves, ability, bulk.overall),
		bulk,
		attacks: [...strongest.values()],
		abilityBonus: abilityBonus(ability, context.gameType),
		abilityPenalty: ABILITY_PENALTY[ability] ?? 1,
		mega: species.mega,
		// Pokémon that aren't fully evolved hold Eviolite, already counted in their bulk
		holdsItem: !species.needsItem && !species.nfe,
		itemOffence: 1,
		gameType: context.gameType,
	};
}

/** The parts of a Pokémon's value with each of its abilities the format allows */
export function speciesValueParts(data: PokemonData, species: Species, options: ScoringOptions = {}) {
	const context: Context = { data, gameType: options.gameType ?? 'doubles', pool: options.pool };
	const moves = allowedMoves(data, species, options);
	const abilities = species.abilities.map(toID).filter(id => id && (options.abilityAllowed?.(id) ?? true));
	if (!abilities.length) abilities.push('');
	return abilities.map(ability => getParts(context, species, moves, ability));
}

function allowedMoves(data: PokemonData, species: Species, options: ScoringOptions) {
	const moves: MoveInfo[] = [];
	for (const moveId of data.getLearnset(species.id)) {
		if (options.moveAllowed && !options.moveAllowed(moveId)) continue;
		const move = data.getMove(moveId);
		if (move) moves.push(move);
	}
	return moves;
}

/** Works out what a pool is like: how common each type is, and how many of its Pokémon have each utility move */
export function poolContext(data: PokemonData, speciesIds: readonly string[], options: ScoringOptions = {}): PoolContext {
	const species = speciesIds.map(id => data.getSpecies(id)).filter((s): s is Species => !!s);
	const typeCounts: Record<string, number> = {};
	const attackCounts: Record<string, number> = {};
	const moveCounts: Record<string, number> = {};
	for (const s of species) {
		const spreadTypes = new Set<string>();
		for (const type of s.types) {
			typeCounts[type] = (typeCounts[type] ?? 0) + 1;
			attackCounts[type] = (attackCounts[type] ?? 0) + 1;
		}
		for (const move of allowedMoves(data, s, options)) {
			if (utilityValue(move.id) || SINGLES_UTILITY[move.id]) moveCounts[move.id] = (moveCounts[move.id] ?? 0) + 1;
			if (move.spread && move.category !== 'Status' && move.power >= 60) spreadTypes.add(move.type);
		}
		// spread attacks hit both foes, so in doubles they're what Pokémon get hit by most
		if (options.gameType !== 'singles') {
			for (const type of spreadTypes) attackCounts[type] = (attackCounts[type] ?? 0) + 0.5;
		}
	}
	// halfway between every type counting the same and counting as often as it appears
	const weigh = (counts: Record<string, number>) => {
		const average = Object.values(counts).reduce((total, count) => total + count, 0) / data.types.length || 1;
		return Object.fromEntries(data.types.map(type => [type, 0.5 + 0.5 * (counts[type] ?? 0) / average]));
	};
	const typeWeights = weigh(typeCounts);
	const attackWeights = weigh(attackCounts);
	const moveShares: Record<string, number> = {};
	for (const [id, count] of Object.entries(moveCounts)) moveShares[id] = count / (species.length || 1);
	return { typeWeights, attackWeights, moveShares };
}

/** Values a Pokémon on its own (higher is better), with the best of its abilities and items */
export function scoreSpecies(data: PokemonData, species: Species, options: ScoringOptions = {}): SpeciesScore {
	let best: SpeciesScore | null = null;
	for (const parts of speciesValueParts(data, species, options)) {
		const value = partsValue(parts);
		if (best && value <= best.value) continue;
		best = toScore(parts, value);
	}
	return best!;
}

function toScore(parts: ValueParts, value: number, weights = VALUE_WEIGHTS, attackScale = 1): SpeciesScore {
	return {
		value, offence: parts.offence, support: supportTotal(parts.supportMoves), bulk: parts.bulk.overall,
		speed: speedFactor(parts.speedStat, parts.baseSpeed, parts.gameType), ability: parts.ability,
		item: bestItem(parts, weights, attackScale),
	};
}

/* Megas */

/**
 * How hard a Pokémon hits, with its Speed, holding a Life Orb if it can (Choice items lock it into one move, which
 * costs too much in doubles to compare with): what a Mega Evolution has to beat
 */
function attackingPower(parts: ValueParts) {
	const item = parts.holdsItem ? ITEMS.lifeorb.offence : 1;
	return parts.offence * item * speedFactor(parts.speedStat, parts.baseSpeed, parts.gameType);
}

/**
 * A Mega is worth most when it does something its base forme can't: Mega Salamence gets strong Flying STABs (spread
 * Hyper Voice, Double-Edge) where Salamence only has Dual Wingbeat. So it gains weights.megaUpgrade for each unit of
 * attacking power it adds. Megas also give up their held item, so a Mega that isn't clearly better than the forme it
 * changes from holding an item (by weights.megaMargin) loses the difference: Garchomp with a Life Orb outdamages and
 * outspeeds Mega Garchomp. But a player can always leave it in its base forme, holding the Mega Stone (Sableye keeps
 * Prankster that way), so it's never worth less than its base forme with no other item.
 */
export function megaValue(value: number, parts: ValueParts, base: { parts: ValueParts, value: number } | undefined, weights = VALUE_WEIGHTS) {
	if (!base) return value;
	const upgrade = attackingPower(parts) - attackingPower(base.parts);
	const mega = value + weights.megaUpgrade * Math.max(0, upgrade) - Math.max(0, base.value + weights.megaMargin - value);
	return Math.max(mega, valueWithItem(base.parts, '', weights));
}

/** Lowers the values of a pool's lesser Megas, which teams with a better Mega would rarely use (see megaSlots) */
export function megaCompetition(values: readonly number[], isMega: readonly boolean[], weights = VALUE_WEIGHTS) {
	const megas = values.map((value, i) => i).filter(i => isMega[i]).sort((a, b) => values[b]! - values[a]!);
	const adjusted = [...values];
	for (const [rank, i] of megas.entries()) {
		const beyond = rank - weights.megaSlots;
		if (beyond > 0) adjusted[i] = values[i]! * Math.max(weights.megaFloor, 1 - weights.megaDecay * beyond);
	}
	return adjusted;
}

/* The meta */

const hitShares = new WeakMap<ValueParts, Map<ValueParts, number>>();

/**
 * How much of the target's HP an attacker's best hit does (1 = a KO), counting the attacker's item and Intimidate on
 * physical attacks
 */
export function hitShare(data: PokemonData, attacker: ValueParts, target: ValueParts) {
	let cache = hitShares.get(attacker);
	if (!cache) hitShares.set(attacker, cache = new Map());
	const cached = cache.get(target);
	if (cached !== undefined) return cached;
	let best = 0;
	for (const attack of attacker.attacks) {
		const multiplier = moveMultiplier(data, { ...attack.move, type: attack.type }, target.types, target.ability);
		if (!multiplier) continue;
		const physical = attack.category === 'Physical';
		const defence = physical ? target.bulk.def : target.bulk.spd;
		// Intimidate takes a stage off the attacker's Attack
		const intimidated = physical && target.ability === 'intimidate' ? 2 / 3 : 1;
		best = Math.max(best, DAMAGE_SCALE * attack.perTarget * attacker.itemOffence * multiplier * intimidated /
			(defence * target.bulk.hp));
	}
	best = Math.min(best, 1.2);
	cache.set(target, best);
	return best;
}

/** In doubles, a strong spread attack hits both foes, so its attacks count for more against the meta */
function spreading(parts: ValueParts) {
	if (parts.gameType !== 'doubles' || !parts.attacks.length) return 1;
	const best = Math.max(...parts.attacks.map(a => a.perTarget));
	return parts.attacks.some(a => a.move.spread && a.perTarget >= 0.7 * best) ? 1.2 : 1;
}

/** How a Pokémon fares against the meta's best Pokémon, and how well it hits the Pokémon that wall them */
interface MetaScore {
	/** How much of their HP its best hits do, on average (more if it's faster) */
	dealt: number;
	/** How much of its HP their best hits do, on average (more if they're faster) */
	taken: number;
	/** How much of their walls' HP its best hits do, on average */
	partnering: number;
	/** The share of the pool's Pokémon worth drafting that it can't hurt much: how easily it's walled */
	walled: number;
	/** The share of them that hit it hard (over 60% of its HP): how easily it's threatened */
	threatened: number;
}

/**
 * Works out each Pokémon's matchups against the meta (the pool's `metaSize` best Pokémon, the best counting most)
 * and how well it partners them, by hitting the Pokémon among `relevant` that the best Pokémon can't hurt.
 */
function metaScores(
	data: PokemonData, parts: readonly ValueParts[], values: readonly number[], weights: ValueWeights
): MetaScore[] {
	const order = values.map((value, i) => i).sort((a, b) => values[b]! - values[a]!);
	const meta = order.slice(0, weights.metaSize);
	const top = values[order[0]!] || 1;
	const metaWeight = (i: number) => Math.max(values[i]! / top, 0) ** 2;
	// the Pokémon worth drafting: the ones the meta has to get past
	const relevant = order.slice(0, Math.max(weights.metaSize * 5, 40));
	const walls = new Map<number, number[]>();
	for (const t of meta) {
		walls.set(t, relevant.filter(w => w !== t && hitShare(data, parts[t]!, parts[w]!) < 0.35));
	}
	return parts.map((p, i) => {
		let dealt = 0;
		let taken = 0;
		let partnering = 0;
		let total = 0;
		let partnerTotal = 0;
		for (const t of meta) {
			if (t === i || parts[t]!.species === p.species) continue;
			const weight = metaWeight(t);
			const faster = p.speedStat > parts[t]!.speedStat;
			dealt += weight * hitShare(data, p, parts[t]!) * spreading(p) * (faster ? 1 : 0.75);
			taken += weight * hitShare(data, parts[t]!, p) * (faster ? 0.75 : 1);
			total += weight;
			const tWalls = walls.get(t)!.filter(w => w !== i);
			if (tWalls.length) {
				partnering += weight * spreading(p) *
					tWalls.reduce((sum, w) => sum + Math.min(hitShare(data, p, parts[w]!), 1), 0) / tWalls.length;
				partnerTotal += weight;
			}
		}
		const others = relevant.filter(w => w !== i);
		const walled = others.filter(w => hitShare(data, p, parts[w]!) < 0.35).length / (others.length || 1);
		const threatened = others.filter(w => hitShare(data, parts[w]!, p) > 0.6).length / (others.length || 1);
		return {
			dealt: total ? dealt / total : 0, taken: total ? taken / total : 0,
			partnering: partnerTotal ? partnering / partnerTotal : 0, walled, threatened,
		};
	});
}

/** A Pokémon's value in a pool: on its own, then against the pool's meta */
export interface PoolValue {
	id: string;
	value: number;
	score: SpeciesScore;
	parts: ValueParts;
}

/** A pool's Pokémon with the parts of their value for each ability, worked out once and valued with any weights */
export interface PoolParts {
	species: Species[];
	/** For each Pokémon, its parts with each ability */
	parts: ValueParts[][];
	/** For each Mega, the parts of the forme it changes from */
	megaBases: (ValueParts[] | null)[];
}

export function poolParts(data: PokemonData, speciesIds: readonly string[], options: ScoringOptions = {}): PoolParts {
	const species = speciesIds.map(id => data.getSpecies(id)).filter((s): s is Species => !!s);
	const pool = options.pool ?? poolContext(data, speciesIds, options);
	const scoring = { ...options, pool };
	return {
		species,
		parts: species.map(s => speciesValueParts(data, s, scoring)),
		megaBases: species.map(s => {
			const base = s.mega && s.changesFrom ? data.getSpecies(s.changesFrom) : null;
			return base ? speciesValueParts(data, base, scoring) : null;
		}),
	};
}

/**
 * Values each Pokémon in a pool: on its own (with its best ability and item, and Megas compared with their base
 * formes), then against the pool's meta. Pokémon the data doesn't have are left out.
 */
export function poolValues(
	data: PokemonData, speciesIds: readonly string[], options: ScoringOptions = {}, weights = VALUE_WEIGHTS
): PoolValue[] {
	return valuePool(data, poolParts(data, speciesIds, options), weights);
}

/** Values a pool from its parts (see poolValues) */
export function valuePool(data: PokemonData, pool: PoolParts, weights = VALUE_WEIGHTS): PoolValue[] {
	const { species } = pool;
	const best = (candidates: readonly ValueParts[]) => {
		let result: { parts: ValueParts, value: number } | null = null;
		for (const parts of candidates) {
			const value = partsValue(parts, weights);
			if (!result || value > result.value) result = { parts, value };
		}
		return result!;
	};
	const own = pool.parts.map(best);
	const bases = pool.megaBases.map(base => base ? best(base) : undefined);
	let values = species.map((s, i) => megaValue(own[i]!.value, own[i]!.parts, bases[i], weights));
	values = megaCompetition(values, species.map(s => s.mega), weights);

	const parts = own.map(o => o.parts);
	for (const p of parts) {
		const item = bestItem(p, weights);
		p.itemOffence = item ? 1 + (ITEMS[item].offence - 1) * weights.item : 1;
	}
	const meta = metaScores(data, parts, values, weights);
	// centred, so the meta moves Pokémon up and down without changing the overall scale
	const mean = (key: keyof MetaScore) => meta.reduce((total, m) => total + m[key], 0) / (meta.length || 1);
	const means = {
		dealt: mean('dealt'), taken: mean('taken'), partnering: mean('partnering'), walled: mean('walled'),
		threatened: mean('threatened'),
	};
	// Intimidate only helps against physical attackers, so it's worth less if the meta mostly attacks specially
	const order = values.map((value, i) => i).sort((a, b) => values[b]! - values[a]!).slice(0, weights.metaSize);
	const physical = order.filter(i => {
		const best = parts[i]!.attacks.reduce<Attack | null>((a, b) => !a || b.perTarget > a.perTarget ? b : a, null);
		return best?.category === 'Physical';
	}).length / (order.length || 1);

	let final = species.map((s, i) => {
		const m = meta[i]!;
		// hitting the meta and its walls makes its attacks worth more; it doesn't change what its support moves do
		const attackScale = 1 + weights.matchups * (m.dealt - means.dealt) + weights.partnering * (m.partnering - means.partnering) -
			weights.counters * (m.walled - means.walled);
		// everyone has to survive the meta's attacks, and is worth less if much of the pool threatens it
		const survival = -weights.survival * (m.taken - means.taken) - weights.threats * (m.threatened - means.threatened);
		// it still weakens every physical attacker in the pool, so it keeps at least half its value
		const intimidate = parts[i]!.ability === 'intimidate' ? -weights.ability * parts[i]!.abilityBonus * (1 - physical) / 2 : 0;
		const value = partsValue(parts[i]!, weights, attackScale) + survival + intimidate;
		return megaValue(value, parts[i]!, bases[i], weights);
	});
	final = megaCompetition(final, species.map(s => s.mega), weights);
	return species.map((s, i) => {
		const m = meta[i]!;
		const attackScale = 1 + weights.matchups * (m.dealt - means.dealt) + weights.partnering * (m.partnering - means.partnering) -
			weights.counters * (m.walled - means.walled);
		return {
			id: s.id, value: final[i]!, parts: parts[i]!,
			score: {
				...toScore(parts[i]!, final[i]!, weights, attackScale),
				meta: { dealt: m.dealt, taken: m.taken, partnering: m.partnering, walled: m.walled, threatened: m.threatened },
			},
		};
	});
}

/** Turns a list of numbers into percentile ranks from 0 to 1 (ties share the middle of their ranks) */
function percentileRanks(values: number[]) {
	const sorted = [...values].sort((a, b) => a - b);
	const first = new Map<number, number>();
	const last = new Map<number, number>();
	for (const [i, value] of sorted.entries()) {
		if (!first.has(value)) first.set(value, i);
		last.set(value, i);
	}
	return values.map(value => sorted.length > 1 ? (first.get(value)! + last.get(value)!) / 2 / (sorted.length - 1) : 1);
}

/**
 * Prices Pokémon from minPoints to maxPoints, based on how their value compares with the rest of `speciesIds`
 * (see poolValues), shaped like a hand-made board (see BOARD_SHAPE). Pokémon the data doesn't have are left out.
 */
export function autoPoints(data: PokemonData, speciesIds: readonly string[], options: PricingOptions) {
	const values = poolValues(data, speciesIds, options);
	const ranks = percentileRanks(values.map(v => v.value));
	const points = new Map<string, number>();
	const { minPoints, maxPoints } = options;
	for (const [i, v] of values.entries()) {
		points.set(v.id, Math.round(minPoints + (maxPoints - minPoints) * boardShare(ranks[i]!)));
	}
	if ((options.gameType ?? 'doubles') === 'doubles') {
		// OgreClan's verdicts, scaled from a 1-20 board to this one; they move only the Pokémon they name
		const scale = (maxPoints - minPoints) / 19;
		for (const [id, change] of Object.entries(BOARD_FEEDBACK)) {
			const current = points.get(id);
			if (current !== undefined) points.set(id, clamp(Math.round(current + change * scale), minPoints, maxPoints));
		}
	}
	// a Mega that plays a different role from its base forme (Mega Dragonite attacks specially) does something the
	// base can't, so it costs at least as much
	for (const v of values) {
		const species = data.getSpecies(v.id);
		const base = species?.mega ? values.find(other => other.id === species.changesFrom) : undefined;
		if (!base || attackCategory(v.parts) === attackCategory(base.parts)) continue;
		points.set(v.id, Math.max(points.get(v.id)!, points.get(base.id)!));
	}
	return points;
}

/** Whether a Pokémon's strongest attack is physical or special */
function attackCategory(parts: ValueParts) {
	return parts.attacks.reduce<Attack | null>((a, b) => !a || b.damage > a.damage ? b : a, null)?.category ?? '';
}

/**
 * OgreClan's own verdicts on doubles Pokémon the rules above misjudge, in points on a 1-20 board, applied after
 * pricing so they don't move any other Pokémon (Larry's feedback on the Reg M-C board, 2026-10-09):
 * - Gholdengo: Good as Gold makes it immune to every status move.
 * - Sinistcha, Indeedee-F: redirection (with Hospitality, or Psychic Terrain) is still undervalued in VGC.
 * - Kingambit: a bulky Steel type, Dark STAB into Gengar and Basculegion, Sucker Punch, and Defiant against Intimidate.
 * - Archaludon: Stamina and Electro Shot, a setup sweeper that snowballs.
 * - Mega Staraptor: Contrary Close Combat raises its bulk, with Roost or Tailwind.
 * - Mega Gardevoir: spread Hyper Voice, and Expanding Force beside Indeedee-F.
 * - Mega Blastoise: Fake Out, Shell Smash, Water Spout and Mega Launcher pulse attacks.
 * - Mega Kangaskhan: Parental Bond doubles Fake Out and Power-Up Punch.
 * - Mega Swampert: Swift Swim beside Pelipper and Archaludon.
 * - Mega Glimmora: not as good as its damage suggests.
 * Each of them comes from a combination of tools, which the rules value less than one or two big ones.
 */
export const BOARD_FEEDBACK: Readonly<Record<string, number>> = {
	gholdengo: 2, sinistcha: 2, indeedeef: 2, kingambit: 3, archaludon: 3, staraptormega: 3, gardevoirmega: 3,
	blastoisemega: 3, kangaskhanmega: 2, swampertmega: 2, glimmoramega: -3,
};
